export const DEFAULT_MOBILE_FIX_TYPE = 'RepairFix'
export const CUSTOMER_SEARCH_PER_PAGE = 20
export const PHOTO_MAX_COUNT = 20
export const PHOTO_MAX_EDGE = 1920
export const PHOTO_JPEG_QUALITY = 0.8
export const CUSTOMER_SEARCH_DEBOUNCE_MS = 300

export const PHOTO_STATUS = {
  UPLOADING: 'uploading',
  SUCCESS: 'success',
  FAILURE: 'failure'
}

export function createRequestId () {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : null
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID()
  }

  if (!cryptoApi || typeof cryptoApi.getRandomValues !== 'function') {
    throw new Error('Secure UUID generation is unavailable')
  }

  const bytes = new Uint8Array(16)
  cryptoApi.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0'))
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join('')
  ].join('-')
}

export function debounce (fn, wait) {
  let timer = null
  const debounced = function () {
    const ctx = this
    const args = arguments
    clearTimeout(timer)
    timer = setTimeout(function () {
      fn.apply(ctx, args)
    }, wait)
  }
  debounced.cancel = function () {
    clearTimeout(timer)
    timer = null
  }
  return debounced
}

export function scaleToMaxEdge (width, height, maxEdge) {
  if (!width || !height || !maxEdge || maxEdge <= 0) {
    return { width: width, height: height }
  }
  const longEdge = Math.max(width, height)
  if (longEdge <= maxEdge) {
    return { width: width, height: height }
  }
  const scale = maxEdge / longEdge
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale)
  }
}

export function jpegFileName (name) {
  const raw = (name || 'photo').replace(/\.[^.]+$/, '')
  return (raw || 'photo') + '.jpg'
}

export function compressImageFile (file, maxEdge, quality) {
  const edge = maxEdge || PHOTO_MAX_EDGE
  const jpegQuality = quality == null ? PHOTO_JPEG_QUALITY : quality

  return new Promise(function (resolve, reject) {
    if (!file) {
      reject(new Error('missing file'))
      return
    }

    const img = new Image()
    const objectUrl = URL.createObjectURL(file)
    img.onload = function () {
      const size = scaleToMaxEdge(img.width, img.height, edge)
      const canvas = document.createElement('canvas')
      canvas.width = size.width
      canvas.height = size.height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        URL.revokeObjectURL(objectUrl)
        reject(new Error('canvas unavailable'))
        return
      }

      try {
        ctx.drawImage(img, 0, 0, size.width, size.height)
      } catch (error) {
        URL.revokeObjectURL(objectUrl)
        reject(error)
        return
      }
      canvas.toBlob(function (blob) {
        URL.revokeObjectURL(objectUrl)
        if (!blob) {
          reject(new Error('compress failed'))
          return
        }
        resolve(new File([blob], jpegFileName(file.name), { type: 'image/jpeg' }))
      }, 'image/jpeg', jpegQuality)
    }
    img.onerror = function () {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('image load failed'))
    }
    img.src = objectUrl
  })
}

export function createPhotoItem (file, localId) {
  return {
    localId: localId || createRequestId(),
    displayName: file ? file.name : '',
    originalFile: file,
    uploadFile: null,
    previewUrl: file ? URL.createObjectURL(file) : '',
    status: PHOTO_STATUS.UPLOADING,
    progress: 0,
    fileId: null,
    fileName: null
  }
}

export function replacePhotoPreviewWithCompressed (photo, compressedFile) {
  if (!photo || !compressedFile) {
    return
  }

  revokePhotoPreview(photo)
  photo.uploadFile = compressedFile
  photo.previewUrl = URL.createObjectURL(compressedFile)
  photo.originalFile = null
}

export function successfulFileIds (photos) {
  return (photos || [])
    .filter(function (photo) {
      return photo && photo.status === PHOTO_STATUS.SUCCESS && photo.fileId
    })
    .map(function (photo) {
      return photo.fileId
    })
}

export function hasSuccessfulPhoto (photos) {
  return successfulFileIds(photos).length >= 1
}

export function isPhotoUploading (photos) {
  return (photos || []).some(function (photo) {
    return photo && photo.status === PHOTO_STATUS.UPLOADING
  })
}

export function revokePhotoPreview (photo) {
  if (photo && photo.previewUrl) {
    URL.revokeObjectURL(photo.previewUrl)
    photo.previewUrl = ''
  }
}

export function buildCustomerSearchParams (search) {
  return {
    SearchCustomer: search || '',
    Page: 1,
    PerPage: CUSTOMER_SEARCH_PER_PAGE
  }
}

export function shouldSearchCustomers (search) {
  return !!(search && String(search).trim())
}

export function buildCreatePayload (requestId, form, fileIds) {
  const source = form || {}
  const immutableFileIds = Object.freeze((fileIds || []).slice())
  const items = Array.isArray(source.engineerWorkItems)
    ? source.engineerWorkItems
    : []
  return Object.freeze({
    requestId: requestId,
    fixType: source.fixType,
    customerId: source.customerId,
    subject: source.subject,
    licensePlate: source.licensePlate,
    serialNumber: source.serialNumber || '',
    firstContactName: source.firstContactName,
    firstContactPhone: source.firstContactPhone,
    secondContactName: source.secondContactName,
    secondContactPhone: source.secondContactPhone,
    thirdContactName: source.thirdContactName,
    thirdContactPhone: source.thirdContactPhone,
    fixReason: source.fixReason,
    fileIds: immutableFileIds,
    engineerWorkItems: Object.freeze(items.slice())
  })
}

export function emptyQuickCreateForm () {
  return {
    fixType: DEFAULT_MOBILE_FIX_TYPE,
    customerId: '',
    shortName: '',
    fullName: '',
    subject: '',
    licensePlate: '',
    firstContactName: '',
    firstContactPhone: '',
    secondContactName: '',
    secondContactPhone: '',
    thirdContactName: '',
    thirdContactPhone: '',
    fixReason: ''
  }
}

export function mapCreateErrorKey (code) {
  switch (code) {
    case 'invalid_request':
      return 'mobile_quick_create_error_invalid_request'
    case 'temp_photo_expired':
      return 'mobile_quick_create_error_temp_photo_expired'
    case 'request_in_progress':
      return 'mobile_quick_create_error_in_progress'
    case 'request_conflict':
      return 'mobile_quick_create_error_conflict'
    case 'create_failed':
      return 'mobile_quick_create_error_create_failed'
    default:
      return 'mobile_quick_create_submit_failed'
  }
}

export function uploadProgressPercent (event) {
  if (!event || !event.total) {
    return 0
  }

  return Math.min(100, Math.round((event.loaded * 100) / event.total))
}
